import { isUnknownArray } from "../types/data";
import type { EntityId, FilmMetadata } from "../types/media";
import type { TmdbPage } from "./tmdbTypes";
import { APP_REQUEST_KEY } from "../secretKeys";
import { getStoredLanguage, getTmdbImageLanguages, getTmdbLanguage } from "../i18n/language";

type CatalogItem = Pick<FilmMetadata, "id" | "release_date" | "first_air_date" | "releaseDate" | "vote_average" | "rate" | "popularity">;

const asArray = <T,>(value: readonly T[] | null | undefined): T[] => (isUnknownArray(value) ? [...value] : []);

export const CATALOG_SORTS = Object.freeze({
  POPULARITY: "popularity_desc",
  YEAR_DESC: "year_desc",
  YEAR_ASC: "year_asc",
  RATING_DESC: "rating_desc",
  RATING_ASC: "rating_asc"
});

type CatalogSort = typeof CATALOG_SORTS[keyof typeof CATALOG_SORTS];
const CATALOG_SORT_VALUES: ReadonlySet<string> = new Set(Object.values(CATALOG_SORTS));

export const normalizeCatalogSort = (value: unknown): CatalogSort =>
  typeof value === "string" && CATALOG_SORT_VALUES.has(value) ? value as CatalogSort : CATALOG_SORTS.POPULARITY;

export const toLocalDateString = (date = new Date()) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, "0"),
  String(date.getDate()).padStart(2, "0")
].join("-");

const getCatalogReleaseDate = (item: CatalogItem) =>
  String(item?.release_date || item?.first_air_date || item?.releaseDate || "").slice(0, 10);

const isReleasedBy = (item: CatalogItem, latestDate: string) => {
  const releaseDate = getCatalogReleaseDate(item);
  // Preserve titles without a known date. Explicit future dates, however,
  // must not leak into a chronological "newest" catalogue.
  return !releaseDate || releaseDate <= latestDate;
};

const getTmdbCatalogSort = (sort: CatalogSort, endpoint: string) => {
  if (sort === CATALOG_SORTS.YEAR_DESC) {
    return endpoint === "tv" ? "first_air_date.desc" : "primary_release_date.desc";
  }
  if (sort === CATALOG_SORTS.YEAR_ASC) {
    return endpoint === "tv" ? "first_air_date.asc" : "primary_release_date.asc";
  }
  if (sort === CATALOG_SORTS.RATING_DESC) return "vote_average.desc";
  if (sort === CATALOG_SORTS.RATING_ASC) return "vote_average.asc";
  return "popularity.desc";
};

const getCatalogSortValue = (item: CatalogItem, sort: CatalogSort) => {
  if (sort === CATALOG_SORTS.YEAR_DESC || sort === CATALOG_SORTS.YEAR_ASC) {
    const timestamp = Date.parse(
      item?.release_date || item?.first_air_date || item?.releaseDate || ""
    );
    return Number.isFinite(timestamp) ? timestamp : null;
  }
  if (sort === CATALOG_SORTS.RATING_DESC || sort === CATALOG_SORTS.RATING_ASC) {
    const rating = Number(item?.vote_average ?? item?.rate);
    return Number.isFinite(rating) && rating > 0 ? rating : null;
  }
  const popularity = Number(item?.popularity);
  return Number.isFinite(popularity) ? popularity : 0;
};

export const compareCatalogItems = (first: CatalogItem, second: CatalogItem, sortValue: unknown) => {
  const sort = normalizeCatalogSort(sortValue);
  const ascending = sort === CATALOG_SORTS.YEAR_ASC || sort === CATALOG_SORTS.RATING_ASC;
  const firstValue = getCatalogSortValue(first, sort);
  const secondValue = getCatalogSortValue(second, sort);
  if (firstValue == null && secondValue == null) {
    return (second.popularity || 0) - (first.popularity || 0);
  }
  if (firstValue == null) return 1;
  if (secondValue == null) return -1;
  const difference = ascending
    ? firstValue - secondValue
    : secondValue - firstValue;
  return difference || (second.popularity || 0) - (first.popularity || 0);
};

const sortCatalogResults = (results: CatalogItem[], sort: CatalogSort) => {
  results.sort((first, second) => compareCatalogItems(first, second, sort));
};

const normalizeRange = (firstValue: unknown, secondValue: unknown, defaultMin: number, defaultMax: number): [number, number] => {
  const first = Number(firstValue);
  const second = Number(secondValue);
  const safeFirst = Number.isFinite(first) ? first : defaultMin;
  const safeSecond = Number.isFinite(second) ? second : defaultMax;
  const clampedFirst = Math.min(Math.max(safeFirst, defaultMin), defaultMax);
  const clampedSecond = Math.min(Math.max(safeSecond, defaultMin), defaultMax);
  return [Math.min(clampedFirst, clampedSecond), Math.max(clampedFirst, clampedSecond)];
};

export type SearchTitle = Omit<FilmMetadata, "media_type" | "mediaType"> & {
  id: string | number;
  known_for_department?: string;
  known_for?: FilmMetadata[];
  profile_path?: string | null;
} & ({ media_type: "movie" | "tv" } | { media_type: "person" });
export type SearchFilm = Extract<SearchTitle, { media_type: "movie" | "tv" }>;
export type SearchPerson = Extract<SearchTitle, { media_type: "person" }>;
export interface CatalogFilters {
  sortBy?: string;
  genreIds?: readonly EntityId[];
  excludedGenreIds?: readonly EntityId[];
  countryCodes?: readonly string[];
  startYear?: number | string;
  endYear?: number | string;
  minRating?: number | string;
  maxRating?: number | string;
}
export interface SearchOptions {
  signal?: AbortSignal;
  pages?: number;
  startPage?: number;
  limit?: number | null;
  includePeople?: boolean;
  language?: string;
  mediaTypes?: string[] | null;
  filters?: CatalogFilters | null;
  returnMeta?: boolean;
}
export interface SearchPage<T extends SearchTitle = SearchTitle> {
  results: T[];
  hasMore: boolean;
}
export function searchTitles(query: string, options: SearchOptions & { returnMeta: true; includePeople: false }): Promise<SearchPage<SearchFilm>>;
export function searchTitles(query: string, options: SearchOptions & { returnMeta: true }): Promise<SearchPage>;
export function searchTitles(query: string, options?: SearchOptions & { returnMeta?: false }): Promise<SearchTitle[]>;
export function searchTitles(query: string, options: SearchOptions): Promise<SearchTitle[] | SearchPage>;
export async function searchTitles(
  query: string,
  {
    signal,
    pages = 3,
    startPage = 1,
    limit = 50,
    includePeople = false,
    language = getStoredLanguage(),
    mediaTypes = null,
    filters = null,
    returnMeta = false
  }: SearchOptions = {}
): Promise<SearchTitle[] | SearchPage> {
  const normalizedQuery = query?.trim();
  const isCatalogSearch = !normalizedQuery;
  const catalogSort = normalizeCatalogSort(filters?.sortBy);

  const tmdbLanguage = getTmdbLanguage(language);
  const imageLanguages = getTmdbImageLanguages(language);
  const normalizedStartPage = Math.max(1, Math.floor(Number(startPage) || 1));
  const pageNumbers = Array.from(
    { length: Math.max(1, Math.floor(Number(pages) || 1)) },
    (_, index) => normalizedStartPage + index
  );
  // TMDB text search requires a query. An empty query uses the matching
  // discover endpoints so the existing search screen can act as a filterable
  // catalogue without introducing a separate page or flow.
  const requestedCatalogTypes: ("movie" | "tv")[] = isUnknownArray(mediaTypes)
    ? mediaTypes.filter((type): type is "movie" | "tv" => type === "movie" || type === "tv")
    : ["movie", "tv"];
  const endpoints: ("movie" | "tv" | "multi")[] = isCatalogSearch
    ? requestedCatalogTypes
    : includePeople
      ? ["multi"]
      : ["movie", "tv"];
  if (endpoints.length === 0) {
    return returnMeta ? { results: [], hasMore: false } : [];
  }
  const requests = endpoints.flatMap((endpoint) =>
    pageNumbers.map(async (page) => {
      const requestPath = isCatalogSearch
        ? `discover/${endpoint}`
        : `search/${endpoint}`;
      const requestUrl = new URL(`https://api.themoviedb.org/3/${requestPath}`);
      requestUrl.searchParams.set("api_key", APP_REQUEST_KEY);
      requestUrl.searchParams.set("language", tmdbLanguage);
      requestUrl.searchParams.set("include_image_language", imageLanguages);
      requestUrl.searchParams.set("include_adult", "false");
      requestUrl.searchParams.set("page", String(page));
      let catalogReleaseDateLimit = "";

      if (isCatalogSearch) {
        requestUrl.searchParams.set(
          "sort_by",
          getTmdbCatalogSort(catalogSort, endpoint)
        );
        const genreIds = asArray(filters?.genreIds).filter(Boolean);
        const excludedGenreIds = asArray(filters?.excludedGenreIds).filter(Boolean);
        const countryCodes = asArray(filters?.countryCodes).filter(Boolean);
        const [startYear, endYear] = normalizeRange(
          filters?.startYear,
          filters?.endYear,
          1800,
          2100
        );
        const [minRating, maxRating] = normalizeRange(
          filters?.minRating,
          filters?.maxRating,
          0,
          10
        );
        const sortsByYear = catalogSort === CATALOG_SORTS.YEAR_DESC ||
          catalogSort === CATALOG_SORTS.YEAR_ASC;
        const sortsByRating = catalogSort === CATALOG_SORTS.RATING_DESC ||
          catalogSort === CATALOG_SORTS.RATING_ASC;
        if (genreIds.length) requestUrl.searchParams.set("with_genres", genreIds.join("|"));
        if (excludedGenreIds.length) {
          requestUrl.searchParams.set("without_genres", excludedGenreIds.join(","));
        }
        if (countryCodes.length) requestUrl.searchParams.set("with_origin_country", countryCodes.join("|"));
        if (sortsByRating) requestUrl.searchParams.set("vote_count.gte", "1");
        if (minRating > 0) {
          requestUrl.searchParams.set("vote_average.gte", String(minRating));
        }
        if (maxRating < 10) {
          requestUrl.searchParams.set("vote_average.lte", String(maxRating));
        }
        if (startYear > 1800 || sortsByYear) {
          requestUrl.searchParams.set(
            endpoint === "tv" ? "first_air_date.gte" : "primary_release_date.gte",
            `${Math.floor(startYear)}-01-01`
          );
        }
        if (endYear < 2100 || sortsByYear) {
          const requestedEndDate = `${Math.floor(endYear)}-12-31`;
          const today = toLocalDateString();
          catalogReleaseDateLimit = sortsByYear && requestedEndDate > today
            ? today
            : requestedEndDate;
          requestUrl.searchParams.set(
            endpoint === "tv" ? "first_air_date.lte" : "primary_release_date.lte",
            catalogReleaseDateLimit
          );
        }
      } else {
        requestUrl.searchParams.set("query", normalizedQuery);
      }

      const response = await fetch(requestUrl.toString(), { signal });
      if (!response.ok) {
        throw new Error(`TMDB ${isCatalogSearch ? "discover" : "search"} failed: ${response.status}`);
      }
      const payload = await response.json() as TmdbPage<SearchTitle>;
      const items = asArray(payload?.results)
        .filter((item) => !catalogReleaseDateLimit || isReleasedBy(item, catalogReleaseDateLimit))
        .filter((item) =>
          !isCatalogSearch && includePeople
            ? ["movie", "tv", "person"].includes(item.media_type)
            : Boolean(item?.id)
        )
        .map((item) => ({
          ...item,
          media_type: endpoint === "multi" ? item.media_type : endpoint
        }));
      const payloadPage = Math.max(1, Number(payload.page) || page);
      const totalPages = Math.min(500, Math.max(payloadPage, Number(payload.total_pages) || payloadPage + 1));
      return { items, hasMore: payloadPage < totalPages };
    })
  );

  const payloads = await Promise.all(requests);
  const uniqueResults = new Map<string, SearchTitle>();
  payloads.flatMap((payload) => payload.items).forEach((item) => {
    uniqueResults.set(`${item.media_type}-${item.id}`, item);
  });

  const results = Array.from(uniqueResults.values());
  if (isCatalogSearch) {
    sortCatalogResults(results, catalogSort);
  } else if (!includePeople) {
    results.sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
  }

  // Multi-search уже ранжируется TMDB по соответствию запросу. Повторная
  // сортировка только по популярности может вытеснить точное совпадение имени.
  const normalizedLimit = limit == null ? Number.POSITIVE_INFINITY : Number(limit);
  const limitedResults = Number.isFinite(normalizedLimit) && normalizedLimit >= 0
    ? results.slice(0, normalizedLimit)
    : results;
  if (returnMeta) {
    return {
      results: limitedResults,
      hasMore: payloads.some((payload) => payload.hasMore)
    };
  }
  return limitedResults;
};
